import { useState } from 'react';

import { toast } from 'sonner';

import { ThreadDownloadDialog } from './thread-download-dialog';
import { ThreadExportProgressDialog } from './thread-export-progress-dialog';
import { DocumentDownloadOptions } from '../../../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDownloadKimAnswer } from '@/api/mutations/useDownloadKimAnswer';
import { useTogglePublishedThread } from '@/api/mutations/useTogglePublishedThread';
import { useGetThreadStatus } from '@/api/queries/useGetThreadStatus';
import { cleanReferencesAndCopyToClipboard } from '@/constants/referencing';
import { useUserContext } from '@/Contexts/User';
import { getTitleFromFlowType } from '@/helpers/kimDisplay';
import { CopyButton } from '@/new/components/copy-button';
import { DownloadButton } from '@/new/components/download-button';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { Artifact, ConfidenceLevelObject, KimMode } from '@/types';

interface AnswerHeaderProps {
  confidenceLevel: ConfidenceLevelObject;
  content: string;
  caseId: string;
  threadId: string;
  messageId: string;
  artifact: Artifact | undefined;
  flowType: KimMode | undefined;
  isLoadingMessages: boolean;
}

export function AnswerHeader({
  confidenceLevel,
  content,
  caseId,
  threadId,
  messageId,
  artifact,
  flowType,
  isLoadingMessages,
}: AnswerHeaderProps) {
  const [downloadStart, setDownloadStart] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [isCreatingExport, setIsCreatingExport] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  const { user } = useUserContext();
  const { downloadAnswer, downloadAnswerWithDocuments } = useDownloadKimAnswer(caseId, threadId, messageId, artifact);
  const { data: threadData } = useGetThreadStatus({ caseId, threadId });
  const { mutate: toggleThreadPublished, isLoading: isToggling } = useTogglePublishedThread(caseId);

  const handleDownloadClick = () => {
    // If there's an artifact, download directly without showing modal
    if (artifact) {
      handleDownloadAnswer();
    } else {
      // Show modal for formatting options
      setShowDownloadModal(true);
    }
  };

  const handleDownloadAnswer = async (options?: DocumentDownloadOptions, includeSourceDocuments?: boolean) => {
    trackCustomEvent(TrackingEventsV2.DownloadKimAnswer, { caseId, threadId, includeSourceDocuments });
    setShowDownloadModal(false);

    if (includeSourceDocuments) {
      // Start async export job
      try {
        setExportError(undefined);
        const result = await downloadAnswer(options, true);

        if (result && 'exportExists' in result) {
          if (result.exportExists) {
            // Export already exists - download immediately
            await downloadAnswerWithDocuments();
          } else {
            // Job in progress or just started - show progress modal
            setIsCreatingExport(true);
            setShowExportProgressModal(true);
          }
        }
      } catch (error) {
        console.error('[AnswerHeader] Export failed:', error);
        setExportError(error instanceof Error ? error.message : 'Failed to start export');
        setShowExportProgressModal(true);
      }
    } else {
      // Original synchronous download
      setDownloadStart(true);

      try {
        await downloadAnswer(options);
      } catch (error) {
        console.error('Download failed:', error);
        toast.error('Download failed. Please try again.');
      } finally {
        // Reset state
        setDownloadStart(false);
        setDownloadComplete(true);
        setTimeout(() => setDownloadComplete(false), 1000);
      }
    }
  };

  const handleExportDownload = async () => {
    try {
      await downloadAnswerWithDocuments();
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    setShowExportProgressModal(false);
    setIsCreatingExport(false);
    setExportError(undefined);
  };

  const copyPageLinkToClipboard = () => {
    trackCustomEvent(TrackingEventsV2.CopyKimPageLink, { caseId, threadId });
    navigator.clipboard.writeText(window.location.href);
    toast.info('Link to page copied to clipboard');
  };

  const handleTogglePublished = () => {
    if (!threadData?.isPublished) {
      trackCustomEvent(TrackingEventsV2.KimThreadPublished, { caseId, threadId });
    }
    toggleThreadPublished(threadId, {
      onSuccess: ({ isPublished }) => {
        toast.success(`Thread ${isPublished ? 'published' : 'unpublished'} successfully`);
      },
      onError: () => {
        toast.error('Failed to toggle thread published status');
      },
    });
  };

  const buttonsDisabled = isLoadingMessages || confidenceLevel.text === 'Low';

  return (
    <div className="flex h-14 items-center justify-between gap-1 border-b border-gray-100 bg-gray-50/50 px-6 py-3">
      <div className="flex items-center gap-3">
        <span className="iconify lucide--lightbulb text-brandSecondary size-5" />
        <h3 className="text-sm font-medium text-gray-800">{getTitleFromFlowType(flowType)}</h3>

        <Tooltip>
          <TooltipTrigger className="ml-2 flex w-fit items-center gap-1.5 rounded-md border bg-white px-2.25 py-1 text-xs text-gray-600">
            <Icon className="lucide--info" />
            <p className="-mb-0.25">Review sources to verify {getTitleFromFlowType(flowType).toLowerCase()}</p>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-sm p-2 px-3 text-gray-800" sideOffset={8}>
            Kim has undergone extensive testing to increase the accuracy of the answers. However, as with all generative
            AI systems, in complex scenarios it could return an inaccurate answer. Crucially, you should check the
            source fact and document in the citation to verify the answer's completeness or accuracy.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center justify-between gap-2">
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="outline" onClick={copyPageLinkToClipboard}>
                <Icon className="lucide--link h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy link to this page.</p>
            </TooltipContent>
          </Tooltip>

          {flowType !== KimMode.ExtractionFlow && (
            <CopyButton
              copyHandler={() => {
                trackCustomEvent(TrackingEventsV2.CopyKimAnswer, { caseId, threadId });
                cleanReferencesAndCopyToClipboard(content);
                toast.success('Copied to clipboard');
              }}
              tooltipText="Copy answer to clipboard."
            />
          )}

          <DownloadButton
            handleDownload={handleDownloadClick}
            downloadStart={downloadStart}
            downloadComplete={downloadComplete}
            tooltipText="Download answer."
            disabled={buttonsDisabled}
          />
        </>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`text-xs ${threadData?.isPublished ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800' : ''}`}
              onClick={handleTogglePublished}
              disabled={isToggling || buttonsDisabled || threadData?.createdById !== user?.id}
            >
              <span className="iconify lucide--users h-3 w-3" />
              <p className="text-xs">{threadData?.isPublished ? 'Unpublish' : 'Publish'}</p>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-56">
            <p className="text-center">
              {threadData?.createdById !== user?.id
                ? 'You can only publish threads you have created.'
                : threadData?.isPublished
                  ? 'Remove from the list of shared questions.'
                  : 'Add to the list of shared questions.'}
            </p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              onClick={() => {
                trackCustomEvent(TrackingEventsV2.KimConfidenceLevel, { caseId, threadId });
              }}
              className={`relative hover:cursor-pointer ${confidenceLevel.backgroundColor} ${confidenceLevel.textColor} rounded-md border text-xs ${confidenceLevel.borderColor} flex items-center gap-2 hover:${confidenceLevel.backgroundColor} hover:${confidenceLevel.borderColor}`}
            >
              <span>
                Confidence: <b className="font-semibold">{confidenceLevel.text}</b>
              </span>
            </Button>
          </TooltipTrigger>

          <TooltipContent className="mr-4" side="bottom">
            {confidenceLevel.tooltip}
          </TooltipContent>
        </Tooltip>
      </div>

      <ThreadDownloadDialog
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadAnswer}
        isLoading={downloadStart}
      />

      <ThreadExportProgressDialog
        isOpen={showExportProgressModal}
        onClose={handleCloseExportModal}
        caseId={caseId}
        threadId={threadId}
        messageId={messageId}
        onDownload={handleExportDownload}
        isCreating={isCreatingExport}
        error={exportError}
      />
    </div>
  );
}
